import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  Target,
  Eye,
  Award,
  Globe2,
  ArrowRight,
} from 'lucide-react';
import { stats } from '../mock';

const API = process.env.REACT_APP_BACKEND_URL;

const DEFAULT_ABOUT = {
  hero_title: 'Reliable Power & Energy Solutions.',
  hero_subtitle:
    'PowerX telah beroperasi lebih dari 10 tahun menyediakan solusi daya dan energi yang andal untuk kebutuhan rumah, bisnis, industri, dan berbagai aplikasi lainnya.',
  section_title: '10+ tahun menghadirkan solusi daya & energi.',
  paragraphs: [
    'PowerX telah beroperasi lebih dari 10 tahun dalam menyediakan solusi daya dan energi yang andal untuk kebutuhan rumah, bisnis, industri, dan berbagai aplikasi lainnya.',
    'Kami menghadirkan rangkaian produk yang mencakup UPS, inverter, battery, solar panel, dan solar lighting system, dengan fokus pada kualitas, keandalan, serta kemudahan penggunaan.',
    'PowerX terus mengembangkan produk dan solusi yang relevan dengan kebutuhan pelanggan, didukung oleh layanan yang responsif dan harga yang kompetitif.',
  ],
  tagline: 'PowerX — Reliable Power & Energy Solutions.',
  vision:
    'Menjadi merek terpercaya dalam menyediakan solusi daya dan energi terbarukan yang andal dan inovatif.',
  mission:
    'Menghadirkan produk dan solusi daya serta energi berkualitas dengan harga yang kompetitif dan didukung layanan purna jual yang andal dan responsif.',
};

const values = [
  {
    icon: Target,
    title: 'Kualitas Terpercaya',
    desc: 'Produk dipilih dan diuji untuk memastikan kualitas dan keandalan.',
  },
  {
    icon: Eye,
    title: 'Fokus pada Pelanggan',
    desc: 'Kami memberikan solusi yang sesuai dengan kebutuhan setiap pelanggan.',
  },
  {
    icon: Award,
    title: 'Terus Berinovasi',
    desc: 'Kami terus mengembangkan produk dan solusi yang lebih baik dan efisien.',
  },
  {
    icon: Globe2,
    title: 'Energi Berkelanjutan',
    desc: 'Kami mendukung solusi daya yang efisien dan ramah lingkungan.',
  },
];

export default function About() {
  const [about, setAbout] = useState(DEFAULT_ABOUT);
  useEffect(() => {
    axios
      .get(`${API}/api/site/about`)
      .then((r) => {
        if (r.data && r.data.hero_title) setAbout({ ...DEFAULT_ABOUT, ...r.data });
      })
      .catch(() => {});
  }, []);
  return (
    <div data-testid="page-about" className="pt-[68px] md:pt-[100px] bg-white">
      {/* Page Hero */}
      <section className="relative h-[60vh] min-h-[460px] bg-neutral-950 overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-40 kenburns"
          style={{
            backgroundImage:
              'url(https://images.unsplash.com/photo-1497366216548-37526070297c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBvZmZpY2V8ZW58MHx8fHwxNzgwNzI0ODM0fDA&ixlib=rb-4.1.0&q=85)',
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/65 to-black/30" />
        <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-12 h-full flex flex-col justify-center text-white">
          <p className="text-xs tracking-[0.25em] text-red-400 font-semibold mb-5">
            TENTANG POWERX
          </p>
          <h1 className="font-display text-5xl md:text-7xl font-bold leading-[1.05] max-w-3xl mb-6">
            {about.hero_title}
          </h1>
          <p className="text-neutral-200 max-w-2xl text-lg leading-relaxed">
            {about.hero_subtitle}
          </p>
        </div>
      </section>

      {/* Intro / Mission */}
      <section className="section-pad">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 grid lg:grid-cols-2 gap-14 items-start">
          <div>
            <p className="text-xs tracking-[0.25em] text-px-red font-semibold mb-3">
              TENTANG POWERX
            </p>
            <h2 className="font-display text-4xl md:text-5xl font-bold text-neutral-900 mb-6 leading-tight">
              {about.section_title}
            </h2>
            <div className="space-y-5 text-neutral-700 leading-relaxed text-[15px]">
              {about.paragraphs.map((p, i) => (
                <p key={i}>{p}</p>
              ))}
              <p className="font-display text-xl font-bold text-neutral-900 pt-2">
                {about.tagline}
              </p>
            </div>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/products"
                className="inline-flex items-center gap-2 bg-neutral-900 hover:bg-px-red text-white text-sm font-semibold px-7 py-3.5 rounded-full transition-colors"
              >
                Jelajahi Produk <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center border border-neutral-300 hover:border-neutral-900 text-neutral-900 text-sm font-semibold px-7 py-3.5 rounded-full transition-colors"
              >
                Hubungi Kami
              </Link>
            </div>
          </div>

          <div className="relative">
            <div className="relative rounded-3xl overflow-hidden aspect-[4/5] bg-neutral-100">
              <img
                src="https://images.pexels.com/photos/17489163/pexels-photo-17489163.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="PowerX facility"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="absolute -bottom-6 -left-6 hidden md:block bg-white shadow-xl rounded-2xl p-6 max-w-[260px] border border-neutral-100">
              <Award className="w-7 h-7 text-px-red mb-2" />
              <p className="font-display font-bold text-neutral-900 text-lg leading-tight mb-1">
                5 Lini Produk
              </p>
              <p className="text-xs text-neutral-600">
                UPS · Inverter · Battery · Solar Panel · Solar Lighting System.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Stats strip */}
      <section className="bg-neutral-950 text-white">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-14 flex flex-wrap justify-center gap-y-10">
          {stats.map((s, i) => (
            <div
              key={i}
              className="text-center border-r last:border-r-0 border-neutral-800 px-8 md:px-16 lg:px-24"
            >
              <div className="font-display text-5xl md:text-6xl font-bold mb-2">
                {(() => {
                  const m = s.value.match(/^([\d/.,]+)(.*)$/);
                  return (
                    <>
                      <span className="text-white">{m?.[1] || s.value}</span>
                      <span className="text-px-red">{m?.[2] || ''}</span>
                    </>
                  );
                })()}
              </div>
              <p className="text-xs md:text-sm text-neutral-400 tracking-wider uppercase">
                {s.label}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Vision & Mission */}
      <section className="section-pad bg-neutral-50">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 grid md:grid-cols-2 gap-6">
          <div className="bg-neutral-900 text-white rounded-3xl p-10">
            <div className="w-12 h-12 rounded-xl bg-px-red/20 flex items-center justify-center mb-5">
              <Eye className="w-6 h-6 text-red-400" />
            </div>
            <h3 className="font-display text-3xl font-bold mb-4">Visi</h3>
            <p className="text-neutral-300 leading-relaxed">
              {about.vision}
            </p>
          </div>
          <div className="bg-white rounded-3xl p-10 border border-neutral-200">
            <div className="w-12 h-12 rounded-xl bg-red-50 flex items-center justify-center mb-5">
              <Target className="w-6 h-6 text-px-red" />
            </div>
            <h3 className="font-display text-3xl font-bold text-neutral-900 mb-4">Misi</h3>
            <p className="text-neutral-700 leading-relaxed">
              {about.mission}
            </p>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="section-pad bg-white">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="max-w-2xl mb-14">
            <p className="text-xs tracking-[0.25em] text-px-red font-semibold mb-3">
              NILAI INTI
            </p>
            <h2 className="font-display text-4xl md:text-5xl font-bold text-neutral-900">
              Prioritas Utama Kami
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {values.map((v, i) => (
              <div
                key={i}
                className="group bg-neutral-50 rounded-2xl p-8 border border-neutral-200 hover-lift"
              >
                <div className="w-12 h-12 rounded-xl bg-white group-hover:bg-px-red flex items-center justify-center mb-5 transition-colors">
                  <v.icon className="w-6 h-6 text-px-red group-hover:text-white transition-colors" />
                </div>
                <h3 className="font-display text-lg font-bold text-neutral-900 mb-2">
                  {v.title}
                </h3>
                <p className="text-sm text-neutral-600 leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative bg-neutral-950 text-white overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-red-700/40 via-neutral-950 to-neutral-950" />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-12 py-20 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <h2 className="font-display text-4xl md:text-5xl font-bold leading-tight mb-4">
              Siap menjaga bisnis Anda tetap menyala?
            </h2>
            <p className="text-neutral-300 max-w-lg">
              Bicara dengan engineer kami untuk mendapatkan rekomendasi UPS atau inverter yang tepat — konsultasi gratis tanpa komitmen.
            </p>
          </div>
          <div className="flex md:justify-end gap-3 flex-wrap">
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 bg-px-red hover:bg-red-700 text-white font-semibold px-7 py-3.5 rounded-full transition-colors"
            >
              Konsultasi Gratis <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/products"
              className="inline-flex items-center border border-white/30 hover:border-white text-white font-semibold px-7 py-3.5 rounded-full transition-colors"
            >
              Lihat Semua Produk
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
